// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a lesson's worksheet on paper (Education tier plan F9, the part
 * that needs nothing stored): the students' copy has the driving question,
 * each step's questions and tasks with room to write, and the exit
 * ticket; the teacher's copy adds the misconception and what is actually
 * true, the teacher notes and why each dataset was chosen. A step that adds
 * real-time variables over its dataset lists them in order, each with what to
 * look for and room to write. Built from the lesson Khipu in the browser
 * and printed (or saved as PDF) from a new tab; nothing is sent anywhere.
 */
import { getLocale, t } from '../i18n'
import { localized, type Khipu, type KhipuText } from '../services/khipu'
import { promptsFor, stepRealtime } from '../services/lessonService'
import { escapeHtml } from './domUtils'

export type WorksheetCopy = 'students' | 'teacher'

const PROMPT_LABEL: Record<string, string> = {
  question: 'lessons.player.prompt.question',
  task: 'lessons.player.prompt.task',
  caption: 'lessons.player.prompt.caption',
  note: 'lessons.player.prompt.note',
}

/** The worksheet as one printable HTML document. */
export function worksheetHtml(k: Khipu, copy: WorksheetCopy, lang = getLocale()): string {
  const text = (value: KhipuText | undefined) => escapeHtml(localized(value, lang))
  const lesson = k.lesson ?? {}
  const byKey = new Map(k.datasets.map((d) => [d.key, d]))
  const label = (kind: string) => escapeHtml(t((PROMPT_LABEL[kind] ?? 'lessons.player.prompt.note') as Parameters<typeof t>[0]))
  const meta = [
    typeof lesson.grade === 'number' ? t('lessons.card.grade', { grade: lesson.grade }) : '',
    localized(lesson.unitTitle, lang),
    (lesson.standards ?? []).map((s) => s.code).join(', '),
  ].filter(Boolean).join(' · ')

  const steps = k.path.steps.map((step) => {
    const d = step.dataset ? byKey.get(step.dataset) : undefined
    const forStudents = promptsFor(step, 'students')
    const forTeacher = copy === 'teacher' ? promptsFor(step, 'presenter') : []
    const variables = stepRealtime(k, step, lang)
    return `<section class="step">
      <h2>${escapeHtml(t('lessons.player.step', { n: step.n, total: k.path.steps.length }))}: ${text(step.title)}</h2>
      ${d ? `<p class="dataset">${escapeHtml(t('lessons.worksheet.dataset'))}: ${text(d.title as KhipuText | undefined)}${d.units ? ` (${escapeHtml(d.units)})` : ''}</p>` : ''}
      ${forStudents.map((p) => `<div class="prompt"><p><strong>${label(p.kind)}</strong> ${escapeHtml(p.text)}</p>${copy === 'students' ? '<div class="lines"></div>' : ''}</div>`).join('')}
      ${variables.length ? `<div class="variables"><p><strong>${escapeHtml(t('lessons.worksheet.variables'))}</strong></p><ol>${variables.map((v) => `<li><p><strong>${escapeHtml(v.title)}</strong>${v.prompt ? ` · ${escapeHtml(t('lessons.player.rt.look'))}: ${escapeHtml(v.prompt)}` : ''}</p>${copy === 'students' && v.prompt ? '<div class="lines short"></div>' : ''}</li>`).join('')}</ol></div>` : ''}
      ${forTeacher.length ? `<div class="teacher"><p><strong>${escapeHtml(t('lessons.worksheet.why'))}</strong></p>${forTeacher.map((p) => `<p>${escapeHtml(p.text)}</p>`).join('')}</div>` : ''}
    </section>`
  }).join('')

  const teacherBlock = copy === 'teacher'
    ? `${lesson.misconception ? `<section><h2>${escapeHtml(t('lessons.detail.misconception'))}</h2><p>“${text(lesson.misconception)}”</p></section>` : ''}
       ${lesson.correction ? `<section><h2>${escapeHtml(t('lessons.detail.correction'))}</h2><p>${text(lesson.correction)}</p></section>` : ''}
       ${(lesson.teacherNotes ?? []).length ? `<section><h2>${escapeHtml(t('lessons.detail.teacherNotes'))}</h2><ul>${(lesson.teacherNotes ?? []).map((n) => `<li>${text(n)}</li>`).join('')}</ul></section>` : ''}`
    : ''

  return `<!doctype html>
<html lang="${escapeHtml(lang)}">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${text(k.title)} — ${escapeHtml(t(copy === 'teacher' ? 'lessons.worksheet.teacherTitle' : 'lessons.worksheet.title'))}</title>
<style>
  body { font: 12pt/1.45 system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; color: #111; background: #fff; margin: 0 auto; max-width: 46rem; padding: 1.5rem; }
  h1 { font-size: 18pt; margin: 0 0 0.2rem; }
  h2 { font-size: 13pt; margin: 1.2rem 0 0.3rem; }
  .meta, .dataset, .notice { color: #444; font-size: 10pt; }
  .who { display: flex; gap: 1rem; margin: 0.8rem 0 1rem; }
  .who span { flex: 1; border-bottom: 1px solid #555; padding-bottom: 0.2rem; font-size: 10pt; color: #444; }
  .driving { font-size: 13pt; font-weight: 600; }
  .prompt p { margin: 0.4rem 0 0.2rem; }
  .lines { height: 5.2rem; background: repeating-linear-gradient(transparent 0 1.25rem, #bbb 1.25rem calc(1.25rem + 1px)); margin-bottom: 0.4rem; }
  .exit .lines { height: 6.5rem; }
  .lines.short { height: 2.6rem; }
  .variables ol { margin: 0.2rem 0; padding-inline-start: 1.4rem; }
  .variables li p { margin: 0.3rem 0 0.1rem; }
  .teacher { border-inline-start: 3px solid #2e7d32; padding-inline-start: 0.6rem; color: #333; font-size: 10.5pt; }
  .step { break-inside: avoid; }
  .notice { margin-top: 1.5rem; border-top: 1px solid #ccc; padding-top: 0.5rem; }
  @media print { body { padding: 0; } @page { margin: 1.6cm; } }
</style>
</head>
<body>
  <h1>${text(k.title)}</h1>
  <p class="meta">${escapeHtml(meta)}${copy === 'teacher' ? ` · ${escapeHtml(t('lessons.worksheet.teacherTitle'))}` : ''}</p>
  ${copy === 'students' ? `<div class="who"><span>${escapeHtml(t('lessons.worksheet.name'))}</span><span>${escapeHtml(t('lessons.worksheet.date'))}</span></div>` : ''}
  ${lesson.drivingQuestion ? `<p class="driving">${text(lesson.drivingQuestion)}</p>` : ''}
  ${steps}
  ${lesson.exitTicket ? `<section class="exit"><h2>${escapeHtml(t('lessons.detail.exitTicket'))}</h2><p>${text(lesson.exitTicket)}</p>${copy === 'students' ? '<div class="lines"></div>' : ''}</section>` : ''}
  ${teacherBlock}
  <p class="notice">${escapeHtml(t('lessons.notice'))}</p>
</body>
</html>`
}

/**
 * Open the worksheet in a new tab and ask to print it there (where the
 * browser offers "Save as PDF" too). A blocked tab falls back to saving
 * the page as a file.
 */
export function printWorksheet(k: Khipu, copy: WorksheetCopy): void {
  const blob = new Blob([worksheetHtml(k, copy)], { type: 'text/html' })
  const url = URL.createObjectURL(blob)
  const tab = window.open(url, '_blank')
  if (tab) {
    tab.addEventListener('load', () => {
      try {
        tab.focus()
        tab.print()
      } catch {
        /* the tab is open; printing is the reader's */
      }
    }, { once: true })
  } else {
    const a = document.createElement('a')
    a.href = url
    a.download = `ayni-lesson-${k.slug ?? k.id}-${copy === 'teacher' ? 'teacher' : 'worksheet'}.html`
    document.body.appendChild(a)
    a.click()
    a.remove()
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}
